'use client';

import { cn } from '@/lib/utils';
import { GlowEffect } from '@/components/motion-primitives/glow-effect';
import { motion } from 'motion/react';
import { forwardRef } from 'react';

export interface GlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  glowEffect?: boolean;
  glowColors?: string[];
  glowMode?: 'rotate' | 'pulse' | 'breathe' | 'colorShift' | 'flowHorizontal' | 'static';
  glowBlur?: 'softest' | 'soft' | 'medium' | 'strong' | 'stronger' | 'strongest' | 'none';
  liquidGlass?: boolean;
  asChild?: boolean;
  children: React.ReactNode;
}

const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    glowEffect = true,
    glowColors,
    glowMode = 'pulse',
    glowBlur = 'medium',
    liquidGlass = true,
    asChild = false,
    children,
    disabled,
    ...props
  }, ref) => {
    // Default glow colors based on variant
    const getDefaultGlowColors = () => {
      switch (variant) {
        case 'primary':
          return ['#FCD34D', '#CA8A04', '#A16207', '#713F12']; // Luxury Gold
        case 'secondary':
          return ['#78716C', '#44403C', '#292524', '#1C1917']; // Deep Stone
        case 'success':
          return ['#FACC15', '#CA8A04', '#854D0E', '#422006']; // Deep Gold/Bronze
        case 'danger':
          return ['#b91c1c', '#7f1d1d', '#450a0a', '#1C1917']; // Refined Crimson/Dark
        case 'ghost':
          return ['#FAFAF9', '#E7E5E4', '#A8A29E', '#78716C']; // Light Silver
        default:
          return ['#FCD34D', '#CA8A04', '#A16207', '#713F12'];
      }
    };

    const baseStyles = cn(
      // Base button styles using design system
      'relative inline-flex items-center justify-center rounded-full',
      'font-medium transition-colors duration-300',
      'border border-stone-200/50 dark:border-white/10',
      'backdrop-blur-xl',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-teal/30',
      
      // Glass morphism base
      'bg-white/80 dark:bg-stone-900/40',
      'shadow-medium dark:shadow-premium',
      
      // Liquid glass effect
      liquidGlass && 'before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r before:from-white/20 before:via-transparent before:to-white/20 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300',
      
      // Size variants
      {
        'px-4 py-1.5 text-sm': size === 'sm',
        'px-6 py-2.5 text-base': size === 'md',
        'px-8 py-3.5 text-lg': size === 'lg',
        'px-10 py-4 text-xl': size === 'xl',
      },
      
      // Variant styles
      {
        'text-text-primary bg-brand-teal hover:bg-brand-teal-light border-brand-teal/50 shadow-elevated': variant === 'primary',
        'text-text-primary hover:bg-bg-secondary border-border-primary': variant === 'secondary',
        'text-brand-teal hover:bg-brand-teal/10 border-brand-teal/30': variant === 'success',
        'text-brand-plum hover:bg-brand-plum/10 border-brand-plum/30': variant === 'danger',
        'text-text-secondary hover:bg-bg-secondary border-transparent': variant === 'ghost',
      },
      
      className
    );

    const ButtonComponent: any = asChild ? motion.div : motion.button;

    return (
      <ButtonComponent
        ref={ref}
        className={baseStyles}
        disabled={disabled}
        whileHover={{ y: -2, scale: 1.01 }}
        whileTap={{ y: 0, scale: 0.96 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
        {...props}
      >
        {/* Glow Effect Background */}
        {glowEffect && !disabled && (
          <GlowEffect
            colors={glowColors || getDefaultGlowColors()}
            mode={glowMode}
            blur={glowBlur}
            className="rounded-inherit"
          />
        )}
        
        {/* Button Content */}
        <span className="relative z-10 flex items-center justify-center gap-2">
          {children}
        </span>
        
        {/* Glass reflection overlay */}
        {liquidGlass && (
          <div className="absolute inset-0 rounded-inherit bg-gradient-to-br from-white/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        )}
      </ButtonComponent>
    );
  }
);

GlassButton.displayName = 'GlassButton';

export default GlassButton;
